import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, List, Tag, Button, Typography, Space, Spin } from 'antd';
import { 
  FileTextOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketService, Ticket } from '../services/ticketService';

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const ticketData = await ticketService.getTickets({ limit: 10 });
      
      // 安全检查：确保ticketData是数组
      const safeTicketData = Array.isArray(ticketData) ? ticketData : [];
      console.log('Dashboard获取到的工单数据:', safeTicketData);
      
      setTickets(safeTicketData);
      
      // 计算统计数据
      const stats = {
        total: safeTicketData.length,
        pending: safeTicketData.filter(t => t.status === 'pending').length,
        approved: safeTicketData.filter(t => t.status === 'approved').length,
        rejected: safeTicketData.filter(t => t.status === 'rejected').length
      };
      setStats(stats);
    } catch (error) {
      console.error('加载仪表板数据失败:', error);
      // 设置空数组避免崩溃
      setTickets([]);
      setStats({ total: 0, pending: 0, approved: 0, rejected: 0 });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      executed: 'blue',
      failed: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      pending: '待审批',
      approved: '已通过',
      rejected: '已拒绝',
      executed: '已执行',
      failed: '执行失败'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'blue',
      normal: 'default',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const getPriorityText = (priority: string) => {
    const texts = {
      low: '低',
      normal: '普通',
      high: '高',
      urgent: '紧急'
    };
    return texts[priority as keyof typeof texts] || priority;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          欢迎回来，{user?.name}！
        </Title>
        <Text type="secondary">
          这里是您的工单审批概览
        </Text>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="总工单数"
              value={stats.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="待审批"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="已通过"
              value={stats.approved}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="已拒绝"
              value={stats.rejected}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 最近工单 */}
      <Card 
        title="最近工单" 
        extra={
          <Button 
            type="link" 
            icon={<RightOutlined />}
            onClick={() => navigate('/tickets')}
          >
            查看全部
          </Button>
        }
      >
        <List
          dataSource={tickets}
          renderItem={(ticket) => (
            <List.Item
              key={ticket.id}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              actions={[
                <Button type="link" size="small">
                  查看详情
                </Button>
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{ticket.title}</Text>
                    <Tag color="blue" style={{ fontSize: '12px' }}>
                      {ticket.dms_ticket_code}
                    </Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <div 
                      className="ticket-description" 
                      style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.45)', maxHeight: '60px', overflow: 'hidden' }}
                      dangerouslySetInnerHTML={{
                        __html: ticket.description ? 
                          ticket.description.replace(/```sql\n([\s\S]*?)\n```/g, '<pre><code>$1</code></pre>') :
                          '暂无描述'
                      }}
                    />
                    <Space size={16}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        提交人: {ticket.submitter_name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(ticket.created_at).toLocaleString()}
                      </Text>
                    </Space>
                  </Space>
                }
              />
              <div>
                <Space direction="vertical" align="end">
                  <Tag color={getStatusColor(ticket.status)}>
                    {getStatusText(ticket.status)}
                  </Tag>
                  <Tag color={getPriorityColor(ticket.priority)} style={{ fontSize: '11px' }}>
                    {getPriorityText(ticket.priority)}
                  </Tag>
                </Space>
              </div>
            </List.Item>
          )}
          locale={{ emptyText: '暂无工单数据' }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;

